/**
 * The Certification & Compliance teaser on the Ryerson Commitment landing page
 * (`src/pages/why-ryerson/ryerson-commitment/index.tsx`, its only importer), linking to
 * `/why-ryerson/ryerson-commitment/certification-compliance`. The page itself is
 * `src/pages/why-ryerson/ryerson-commitment/certification-compliance/index.tsx`, built from the
 * section folders beside this file.
 *
 * Its layouts are shared: `./CertificateAndComplianceDesktop.tsx` and
 * `./CertificateAndComplianceMobile.tsx` also render `../ESG/ESG.tsx` and
 * `../RyersonGivesBack/RyersonGivesBack.tsx`, and the mobile one is a pass-through to
 * `../ProductQuality/ProductQualityMobile.tsx`. A change to
 * {@link CertificateAndComplianceProps} or to either layout changes those teasers too.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import {
	CertificateAndComplianceContentDesktop,
	CertificateAndComplianceContentMobile,
} from "./Content";
import CertificateAndComplianceDesktop from "./CertificateAndComplianceDesktop";
import CertificateAndComplianceMobile from "./CertificateAndComplianceMobile";

/** `ContentSection` background type for the teaser band. */
export type backgroundType = "primary" | "secondary";

/** Content of one split-band teaser. `../ESG/ESG.tsx` → `ESGProps` repeats this shape. */
export interface CertificateAndComplianceProps {
	/**
	 * Desktop: `"primary"` makes the split band's right half `white`; anything else, including
	 * unset, `lighterGray`. Mobile: the band type, `"primary"` when unset.
	 */
	background?: backgroundType;
	/**
	 * Heading, image alt text and, slugified (lower-cased, whitespace runs to `-`), the teaser's
	 * anchor id. A `\n` in it renders as a space.
	 */
	title: string;
	content: string;
	imageUrl: string;
	/** Label beside the arrow button; `\n` breaks the line (`white-space: pre-line`). */
	buttonLabel: string;
	/** Site path the teaser links to. */
	moreAboutLink: string;
}

/** Renders the Certification & Compliance teaser in the visitor's language, mobile below `lg`. */
const CertificateAndCompliance: React.FC = () => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<>
			<Media lessThan="lg">
				<CertificateAndComplianceMobile
					{...CertificateAndComplianceContentMobile[language]}
				/>
			</Media>
			<Media greaterThanOrEqual="lg">
				<CertificateAndComplianceDesktop
					{...CertificateAndComplianceContentDesktop[language]}
				/>
			</Media>
		</>
	);
};

export default CertificateAndCompliance;
